import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";

import { siteRoutes } from "@/content/site-routes";
import { generateSeoSuggestions } from "@/lib/seo-assistant.functions";

export const Route = createFileRoute("/panel-seo-asystent")({
  head: () => ({
    meta: [
      { title: "Asystent SEO | Sylwia Sitkowska" },
      {
        name: "description",
        content:
          "Wewnętrzny asystent SEO: propozycje tytułów, opisów, pytań i linków wewnętrznych na podstawie treści podstron.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SeoAssistantPanel,
});

function Copyable({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-lg border border-ink/10 bg-surface p-5">
      <div className="flex items-start justify-between gap-4">
        <p className="text-xs uppercase tracking-[0.18em] text-ink/60">
          {label} · {value.length} znaków
        </p>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(value).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
          className="rounded-full border border-ink/20 px-3 py-1 text-xs text-ink transition hover:border-ink/40"
        >
          {copied ? "Skopiowano" : "Kopiuj"}
        </button>
      </div>
      <p className="mt-2 text-ink">{value}</p>
    </div>
  );
}

function SeoAssistantPanel() {
  const [path, setPath] = useState(siteRoutes[0]?.path ?? "/");
  const run = useServerFn(generateSeoSuggestions);
  const mutation = useMutation({
    mutationFn: (target: string) => run({ data: { path: target } }),
  });

  const result = mutation.data;
  const suggestion = result?.ok ? result.data : null;

  return (
    <main className="mx-auto w-full max-w-4xl px-6 py-16">
      <header className="mb-10">
        <p className="text-xs uppercase tracking-[0.24em] text-ink/50">Monitoring</p>
        <h1 className="mt-2 font-display text-4xl text-ink">Asystent SEO</h1>
        <p className="mt-3 max-w-2xl text-ink/70">
          Wybierz podstronę, a asystent przeczyta jej treść i zaproponuje tytuł, opis, pytania i
          odpowiedzi oraz linki wewnętrzne. Propozycje wymagają Twojej akceptacji, nic nie zmienia się
          automatycznie.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-ink/70" htmlFor="seo-path">
          Podstrona
        </label>
        <select
          id="seo-path"
          value={path}
          onChange={(event) => setPath(event.target.value)}
          className="rounded-full border border-ink/20 bg-surface px-4 py-2 text-sm text-ink"
        >
          {siteRoutes.map((route) => (
            <option key={route.path} value={route.path}>
              {route.path}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => mutation.mutate(path)}
          disabled={mutation.isPending}
          className="rounded-full border border-ink/20 px-4 py-2 text-sm text-ink transition hover:border-ink/40 disabled:opacity-60"
        >
          {mutation.isPending ? "Analizuję…" : "Przygotuj propozycje"}
        </button>
      </div>

      {mutation.isPending && (
        <p className="mt-8 text-ink/60">Czytam treść strony i przygotowuję propozycje…</p>
      )}
      {mutation.isError && (
        <p className="mt-8 rounded-lg border border-ink/10 bg-surface p-5 text-ink/70">
          Nie udało się przygotować propozycji. Spróbuj ponownie.
        </p>
      )}
      {result && !result.ok && (
        <p className="mt-8 rounded-lg border border-ink/10 bg-surface p-5 text-ink/70">{result.message}</p>
      )}

      {suggestion && (
        <div className="mt-10 space-y-10">
          {(result?.ok && (result.currentTitle || result.currentDescription)) && (
            <section>
              <h2 className="font-display text-2xl text-ink">Obecne metadane</h2>
              <div className="mt-4 space-y-2 rounded-lg border border-ink/10 bg-surface p-5 text-sm text-ink/70">
                <p>Title: {result.currentTitle ?? "brak"}</p>
                <p>Opis: {result.currentDescription ?? "brak"}</p>
              </div>
            </section>
          )}

          <section className="space-y-4">
            <h2 className="font-display text-2xl text-ink">Propozycje metadanych</h2>
            <Copyable label="Title" value={suggestion.title} />
            <Copyable label="Meta description" value={suggestion.metaDescription} />
            {suggestion.keywords.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {suggestion.keywords.map((keyword) => (
                  <span
                    key={keyword}
                    className="rounded-full border border-ink/15 px-3 py-1 text-xs text-ink/70"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            )}
          </section>

          {suggestion.faq.length > 0 && (
            <section>
              <h2 className="font-display text-2xl text-ink">Propozycje FAQ</h2>
              <div className="mt-4 space-y-4">
                {suggestion.faq.map((item) => (
                  <div key={item.q} className="rounded-lg border border-ink/10 bg-surface p-5">
                    <p className="font-medium text-ink">{item.q}</p>
                    <p className="mt-2 text-ink/75">{item.a}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {suggestion.internalLinks.length > 0 && (
            <section>
              <h2 className="font-display text-2xl text-ink">Linki wewnętrzne</h2>
              <div className="mt-4 overflow-x-auto rounded-lg border border-ink/10 bg-surface">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-ink/10 text-xs uppercase tracking-[0.14em] text-ink/60">
                    <tr>
                      <th className="p-3">Strona docelowa</th>
                      <th className="p-3">Anchor</th>
                      <th className="p-3">Dlaczego</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggestion.internalLinks.map((link) => (
                      <tr key={`${link.path}-${link.anchor}`} className="border-b border-ink/5 last:border-0">
                        <td className="p-3">{link.path}</td>
                        <td className="p-3">{link.anchor}</td>
                        <td className="p-3 text-ink/70">{link.why}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {suggestion.notes && (
            <section>
              <h2 className="font-display text-2xl text-ink">Uwagi</h2>
              <p className="mt-3 text-ink/75">{suggestion.notes}</p>
            </section>
          )}
        </div>
      )}
    </main>
  );
}
